import {
    computed,
    DestroyRef,
    effect,
    inject,
    Injectable,
    Injector,
    NgZone,
    signal,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { LoggedUserService } from '@core/services/admin/logged-user.service';
import { UsersGlobalService } from '@core/services/store/users-global.service';
import { MessagingIntegrationsService } from '@core/services/third-party-integrations/messaging-integrations.service';
import { ConvoSession } from '@features/inbox/inb-convos/types/sessions.types';
import { SiMessagingHttpService } from '@features/inbox/services/messaging-http.service';
import { SessionThreadPreview } from '@features/inbox/types/threads.model';
import { ToastService } from '@shared/services/toast.service';

import {
    isOutboundVoiceEvent,
    isWhatsappVoiceCall,
    resolveVoiceOrigin,
    VOICE_CONVERSATION_STATE,
    VoiceConversationEvent,
} from '../voice.types';
import { VoiceCallService } from './voice-call.service';
import { VoiceConversationStore } from './voice-conversation.store';
import { VoiceConversationWebsocketService } from './voice-conversation-websocket.service';

/** A call waiting for a human, enriched with whatever the app knows about the caller. */
export interface VoiceWaitingCall {
    event: VoiceConversationEvent;
    /** Contact name when resolved, else the caller number, else ''. */
    name: string;
    /** Raw caller number (E.164) — the ring card pretty-prints it when there is no name. */
    from: string;
    imgUrl?: string;
    /** Line the call came in on (alias or number), blank when unknown. */
    origin: string;
    departmentId?: number;
    /**
     * When the call entered the queue (local epoch ms). Wait seconds are derived from
     * `nowSec` by consumers — entries must NOT carry a precomputed wait or the whole
     * list would be rebuilt every second.
     */
    queuedAtMs: number;
    /** Directed ring: the number is assigned to ME — rings like a phone, not like a queue. */
    forMe: boolean;
    /** Another agent the call is directed to (still pickable by anyone). */
    targetName?: string;
    isWhatsapp: boolean;
}

/** What the ring card / queue rows need from a conversation, resolved once per call. */
interface CallerInfo {
    name: string;
    imgUrl?: string;
    accountAlias?: string;
    voiceNumberId?: number | null;
    departmentId?: number;
}

const SOUND_PREF_KEY = 'voice-queue-sound';
const RING_EVERY_MS = 3500;

/** Server timestamps may arrive without a timezone suffix (naive UTC) — parse as UTC. */
const parseServerTs = (ts: string): number => {
    const hasTz = /(?:Z|[+-]\d{2}:?\d{2})$/.test(ts);
    return Date.parse(hasTz ? ts : ts + 'Z');
};

/**
 * Voice shell of the desktop app — the twin of `MobileVoiceService`. Started once from
 * `MainContentComponent`, so an incoming call rings wherever the agent is (dashboard,
 * audience, settings…) and not only inside the inbox:
 *
 * - keeps the `/topic/voice/conversation` STOMP feed + the Telnyx registration alive
 *   for the whole session, re-syncing the queue from the server on focus / visibility
 *   and on a timer (ghost-row guard);
 * - derives the waiting list (mine first, then FIFO), enriching each call with the
 *   contact behind the conversation through one GET per conversation — the shell sits
 *   above the inbox injector, so it can't read the inbox's in-memory session list;
 * - owns the ambient cues: chime / phone ring, tab-title badge and browser notification;
 * - is the single state the ring card and the queue widget both read (dismissals,
 *   sound preference, expanded queue), so answering or hiding a call in one surface is
 *   reflected in the other.
 *
 * Picking up goes through {@link VoiceCallService}, which lands the inbox on the
 * caller's conversation (`?convo=` deep-link) from wherever the agent was.
 */
@Injectable({ providedIn: 'root' })
export class VoiceShellService {
    private readonly voiceStore = inject(VoiceConversationStore);
    private readonly voiceCallS = inject(VoiceCallService);
    private readonly voiceWs = inject(VoiceConversationWebsocketService);
    private readonly loggedUserS = inject(LoggedUserService);
    private readonly usersS = inject(UsersGlobalService);
    private readonly integrationsS = inject(MessagingIntegrationsService);
    private readonly messagingHttpS = inject(SiMessagingHttpService);
    private readonly translate = inject(TranslateService);
    private readonly toast = inject(ToastService);
    private readonly zone = inject(NgZone);
    private readonly injector = inject(Injector);

    /** Calls (by call_control_id) the agent hid on this tab — the call keeps waiting for others. */
    private readonly dismissed = signal<ReadonlySet<string>>(new Set());
    /** Caller identity per conversation_id, resolved lazily (null = lookup failed, use the number). */
    private readonly callers = signal<Record<number, CallerInfo | null>>({});
    private readonly lookingUp = new Set<number>();

    /** Open browser notifications by conversation_id, closed when the call leaves the queue. */
    private readonly liveNotifications = new Map<number, Notification>();

    /** 1s heartbeat for wait timers — only ticks while a call is waiting. */
    readonly nowSec = signal(Math.floor(Date.now() / 1000));
    readonly soundOn = signal(localStorage.getItem(SOUND_PREF_KEY) !== 'off');
    /** The queue widget's expanded state lives here so the ring card can open it. */
    readonly queueExpanded = signal(false);

    readonly onCall = this.voiceCallS.onCall;
    readonly registered = this.voiceCallS.registered;
    readonly pickingUpId = this.voiceCallS.pickingUpId;

    /** Every waiting call: mine first, then FIFO by queue time. Never recomputes on the 1s tick. */
    readonly waiting = computed<VoiceWaitingCall[]>(() => {
        const dismissed = this.dismissed();
        const callers = this.callers();
        return Object.values(this.voiceStore.events())
            .filter(
                e =>
                    e.state === VOICE_CONVERSATION_STATE.WAITING_AGENT &&
                    !dismissed.has(e.call_control_id) &&
                    // KAN-238: outbound calls are auto-picked by the browser that placed them.
                    !isOutboundVoiceEvent(e) &&
                    // Never offer the call WE are already live on (stale/late WAITING frame).
                    e.conversation_id !== this.voiceCallS.activeCallConversationId()
            )
            .sort((a, b) => Date.parse(a.timestamp) - Date.parse(b.timestamp))
            .map(e => this.toEntry(e, callers[e.conversation_id]))
            .sort((a, b) => Number(b.forMe) - Number(a.forMe));
    });

    readonly count = computed(() => this.waiting().length);
    /** A call directed at the logged user is waiting — ring like a phone. */
    readonly hasCallForMe = computed(() => this.waiting().some(e => e.forMe));
    /** The call the ring card shows — none while the agent is already on a call. */
    readonly incoming = computed<VoiceWaitingCall | null>(() =>
        this.onCall() ? null : (this.waiting()[0] ?? null)
    );
    readonly othersWaiting = computed(() => Math.max(0, this.waiting().length - 1));

    private started = false;
    private stopFns: Array<() => void> = [];

    /** Start the voice shell (idempotent). Call from the desktop root. */
    start(destroyRef: DestroyRef) {
        if (this.started) return;
        this.started = true;

        this.voiceWs.connect();
        // Register the WebRTC client up-front so picking up a call is instant.
        this.voiceCallS.register();
        // The STOMP feed only covers events received while the tab was open — seed the
        // queue from the server so reloads and late-joining agents see who's waiting.
        this.voiceCallS.refreshQueue();

        // Safety net for missed terminal events (dropped STOMP frame, tab asleep…):
        // re-sync on focus regain — hydrateQueue drops rows the server no longer lists.
        const onVisible = () => {
            if (document.visibilityState === 'visible') this.voiceCallS.refreshQueue();
        };
        window.addEventListener('focus', onVisible);
        document.addEventListener('visibilitychange', onVisible);
        this.stopFns.push(() => {
            window.removeEventListener('focus', onVisible);
            document.removeEventListener('visibilitychange', onVisible);
        });

        // All periodic work runs OUTSIDE the Angular zone: a zone-patched setInterval
        // would trigger a full app change-detection pass every second while calls wait.
        // Signal writes still schedule CD, so the surfaces update on real changes only.
        this.zone.runOutsideAngular(() => {
            const tick = setInterval(() => {
                if (this.voiceStore.waitingCount() > 0) {
                    this.nowSec.set(Math.floor(Date.now() / 1000));
                }
            }, 1000);
            // Ghost guard: a directed ring re-checks the server every 10s so an abandoned
            // caller stops ringing fast; generic rows every 30s.
            let resyncTick = 0;
            const resync = setInterval(() => {
                resyncTick++;
                this.voiceStore.purgeStaleWaiting();
                if (
                    this.hasCallForMe() ||
                    (resyncTick % 3 === 0 && this.voiceStore.waitingCount() > 0)
                ) {
                    this.voiceCallS.refreshQueue();
                }
            }, 10_000);
            // Directed ring: while a call targeted at ME waits (and I'm not on a call),
            // keep ringing like an incoming phone — the one-shot chime isn't enough.
            const ring = setInterval(() => {
                if (this.soundOn() && this.incoming()?.forMe) this.playRing();
            }, RING_EVERY_MS);
            this.stopFns.push(
                () => clearInterval(tick),
                () => clearInterval(resync),
                () => clearInterval(ring)
            );
        });

        // Resolve the contact behind every new waiting conversation (once per id).
        const lookupRef = effect(
            () => {
                const events = Object.values(this.voiceStore.events());
                const callers = this.callers();
                for (const e of events) {
                    if (e.state !== VOICE_CONVERSATION_STATE.WAITING_AGENT) continue;
                    if (e.conversation_id in callers) continue;
                    this.lookupCaller(e.conversation_id);
                }
            },
            { injector: this.injector }
        );

        // Ambient cues on every NEW waiting call; drop the notification of calls that left.
        let seen = new Set<number>();
        const cueRef = effect(
            () => {
                const entries = this.waiting();
                const fresh = entries.filter(e => !seen.has(e.event.conversation_id));
                if (fresh.length) {
                    const forMe = fresh.find(e => e.forMe);
                    if (this.soundOn()) {
                        if (forMe) this.playRing();
                        else this.playChime();
                    }
                    this.notify(forMe ?? fresh[0]);
                }
                seen = new Set(entries.map(e => e.event.conversation_id));
                const gone = [...this.liveNotifications.keys()].filter(id => !seen.has(id));
                for (const id of gone) {
                    this.liveNotifications.get(id)?.close();
                    this.liveNotifications.delete(id);
                }
                this.updateTitleBadge(entries.length);
            },
            { injector: this.injector }
        );

        // A wait ran out (QUEUE_TIMEOUT) → the row is already gone; explain why.
        let timedOut = new Set<number>();
        const timeoutRef = effect(
            () => {
                const nowTimedOut = Object.values(this.voiceStore.events())
                    .filter(e => e.state === VOICE_CONVERSATION_STATE.QUEUE_TIMEOUT)
                    .map(e => e.conversation_id);
                if (nowTimedOut.some(id => !timedOut.has(id))) {
                    this.toast.info('voice.call.queue.timedOut');
                }
                timedOut = new Set(nowTimedOut);
            },
            { injector: this.injector }
        );

        // Hang-up with people still waiting → surface the queue right away.
        const endedSub = this.voiceCallS.callEnded$.subscribe(() => {
            if (this.voiceStore.waitingCount() > 0) this.queueExpanded.set(true);
        });

        this.stopFns.push(
            () => lookupRef.destroy(),
            () => cueRef.destroy(),
            () => timeoutRef.destroy(),
            () => endedSub.unsubscribe()
        );

        destroyRef.onDestroy(() => this.stop());
    }

    stop() {
        if (!this.started) return;
        this.started = false;
        this.stopFns.forEach(fn => fn());
        this.stopFns = [];
        this.updateTitleBadge(0);
        this.liveNotifications.forEach(n => n.close());
        this.liveNotifications.clear();
        this.voiceWs.disconnect();
    }

    /* ── actions ───────────────────────────────────────────────── */

    /** WhatsApp calls are always pickable; the rest wait for the Telnyx SDK to register. */
    isPickupReady(call: VoiceWaitingCall): boolean {
        return this.voiceCallS.isPickupReady(call.event);
    }

    isPickingUp(call: VoiceWaitingCall): boolean {
        return this.pickingUpId() === call.event.conversation_id;
    }

    /** Take the call — the inbox lands on the caller's conversation once bridged. */
    answer(call: VoiceWaitingCall) {
        void this.voiceCallS.pickup(call.event, {
            conversationId: call.event.conversation_id,
            contactName: call.name,
            contactImage: call.imgUrl,
            origin: call.origin,
            departmentId: call.departmentId,
            provider: call.event.provider,
        });
    }

    /** Hide the call on this tab only — it keeps waiting for the other agents. */
    dismiss(call: VoiceWaitingCall) {
        this.dismissed.update(set => new Set(set).add(call.event.call_control_id));
    }

    toggleSound() {
        this.soundOn.update(v => !v);
        localStorage.setItem(SOUND_PREF_KEY, this.soundOn() ? 'on' : 'off');
    }

    toggleQueue() {
        this.queueExpanded.update(v => !v);
        // First interaction is a user gesture — the only moment the browser allows
        // asking for notification permission without feeling intrusive.
        this.requestNotificationPermission();
    }

    requestNotificationPermission() {
        if ('Notification' in window && Notification.permission === 'default') {
            void Notification.requestPermission();
        }
    }

    /** Live wait seconds for a call — the only per-second binding in the templates. */
    waitSecOf(call: VoiceWaitingCall): number {
        return Math.max(0, this.nowSec() - Math.floor(call.queuedAtMs / 1000));
    }

    /* ── internals ─────────────────────────────────────────────── */

    private toEntry(event: VoiceConversationEvent, caller?: CallerInfo | null): VoiceWaitingCall {
        const targetId = event.target_user_id ?? null;
        const myId = this.loggedUserS.user()?.user_id;
        const forMe = targetId != null && myId != null && targetId === myId;
        const targetUser =
            !forMe && targetId != null ? this.usersS.getUserById(targetId) : undefined;
        const from = event.from ?? '';
        return {
            event,
            name: caller?.name || from,
            from,
            imgUrl: caller?.imgUrl,
            origin:
                caller?.accountAlias ||
                resolveVoiceOrigin(
                    undefined,
                    caller?.voiceNumberId,
                    this.integrationsS.voiceNumbers()
                ),
            departmentId: caller?.departmentId,
            // Server-seeded queue entry time (queued_at); the STOMP timestamp is the fallback.
            queuedAtMs: parseServerTs(event.queued_at ?? event.timestamp),
            forMe,
            targetName: targetUser?.name,
            isWhatsapp: isWhatsappVoiceCall(event),
        };
    }

    /**
     * Who is calling? The queue feed only carries the number; the conversation's last
     * session has the contact (name, avatar), the line and the department. One GET per
     * waiting conversation, cached for the session; a failure falls back to the number.
     */
    private lookupCaller(conversationId: number) {
        if (this.lookingUp.has(conversationId)) return;
        this.lookingUp.add(conversationId);
        this.messagingHttpS.getConvoSessions$(conversationId).subscribe({
            next: body => {
                const dto = body?.data?.find(s => s.last) ?? body?.data?.[0];
                let info: CallerInfo | null = null;
                if (dto) {
                    try {
                        const preview = new SessionThreadPreview(new ConvoSession(dto));
                        info = {
                            name: preview.title,
                            imgUrl: preview.imgUrl || undefined,
                            accountAlias: preview.accountAlias,
                            voiceNumberId: preview.voice_number_id,
                            departmentId: preview.department_id,
                        };
                    } catch {
                        info = null;
                    }
                }
                this.callers.update(map => ({ ...map, [conversationId]: info }));
                this.lookingUp.delete(conversationId);
            },
            error: () => {
                this.callers.update(map => ({ ...map, [conversationId]: null }));
                this.lookingUp.delete(conversationId);
            },
        });
    }

    /* ── ambient cues ──────────────────────────────────────────── */

    /** Soft two-note chime (E5 → A5) for a generic queue call. */
    private playChime() {
        this.tones(
            [
                [659.25, 0],
                [880, 0.18],
            ],
            0.08,
            0.45
        );
    }

    /** Phone-like double ring (A5-E5 ×2) for calls directed at the logged user. */
    private playRing() {
        this.tones([
            [880, 0],
            [659.25, 0.12],
            [880, 0.45],
            [659.25, 0.57],
        ]);
    }

    /** WebAudio tones — no asset, no autoplay issue once the app was interacted with. */
    private tones(notes: Array<[number, number]>, peak = 0.12, decay = 0.35) {
        try {
            const ctx = new AudioContext();
            for (const [freq, start] of notes) {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'sine';
                osc.frequency.value = freq;
                gain.gain.setValueAtTime(0, ctx.currentTime + start);
                gain.gain.linearRampToValueAtTime(peak, ctx.currentTime + start + 0.02);
                gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + decay);
                osc.connect(gain).connect(ctx.destination);
                osc.start(ctx.currentTime + start);
                osc.stop(ctx.currentTime + start + decay + 0.05);
            }
            setTimeout(() => void ctx.close(), 1500);
        } catch {
            /* audio blocked — the visual cues still cover it */
        }
    }

    /** Browser notification when the tab is hidden and permission was granted. */
    private notify(call: VoiceWaitingCall) {
        if (!('Notification' in window)) return;
        if (Notification.permission !== 'granted' || !document.hidden) return;
        const title = this.translate.instant(
            call.forMe ? 'voice.call.queue.notifTitleForYou' : 'voice.call.queue.notifTitle'
        );
        const body = call.name || this.translate.instant('voice.call.queue.unknownCaller');
        const id = call.event.conversation_id;
        this.liveNotifications.get(id)?.close();
        const n = new Notification(title, {
            body,
            tag: `voice-queue-${id}`,
            // A directed call rings until answered — the notification should stick too
            // (it's closed programmatically the moment the call leaves the queue).
            requireInteraction: call.forMe,
        });
        this.liveNotifications.set(id, n);
        n.onclick = () => {
            window.focus();
            n.close();
            this.liveNotifications.delete(id);
        };
        if (!call.forMe) setTimeout(() => n.close(), 8000);
    }

    /** `(📞n)` prefix on the tab title so the queue is visible from other tabs. */
    private updateTitleBadge(count: number) {
        const base = document.title.replace(/^\(📞\d+\)\s/, '');
        document.title = count > 0 ? `(📞${count}) ${base}` : base;
    }
}
